/**
 * Gap Service
 * 
 * Service for managing GAP (deficiency) records in the ICFR system.
 * Handles all gap-related API calls.
 */

import { apiRequest } from './apiClient';
import { parseCommaSeparated } from '../utils/stringUtils';
import { mapRiskRanking, RiskRanking, RiskRankingApi, SyncfusionSearchParam } from './types';

// ==================== TYPES ====================

export type GapStatus = 'Draft' | 'Open' | 'Archived';
export type GapStatusApi = 'draft' | 'open' | 'archived';
export type GapType = 'Design' | 'Effectiveness' | 'Other';
export type GapTypeApi = 'design' | 'effectiveness' | 'other';
export type PriorityLevel = 'High' | 'Med' | 'Low' | 'Unranked';
export type PriorityLevelApi = 'high' | 'med' | 'low' | 'unranked';

export interface GapCycle {
  id: string;
  code: string;
  name: string;
}

export interface GapProcess {
  id: string;
  code: string;
  name: string;
  riskAssessmentId: string;
}

export interface GapObjective {
  id: string;
  code: string; // Combined format: "ProcessCode.ObjectiveNumber" (e.g., "A01.01")
  processCode: string; // Process code (e.g., "A01")
  objectiveNumber: string; // Objective number (e.g., "01")
}

export interface GapControl {
  id: string;
  code: string;
  name: string;
  type: 'direct' | 'inferred';
}

export interface GapApplication {
  id: string;
  name: string;
}

export interface GapOwner {
  id: string;
  name: string;
  avatar?: string;
}

export interface Gap {
  id: string;
  status: GapStatus;
  gapId: string; // e.g., "A01.001" - Empty if Draft
  cycle: GapCycle;
  linkedProcess: GapProcess;
  relatedObjectives: GapObjective[];
  relatedControls: GapControl[]; // Controls used for compensating (ControlIds/ControlCodes)
  compensatingControls: GapControl[]; // Controls used for compensating (CompensatingControlIds/CompensatingControlCodes)
  effectiveControl: { id: string; code: string } | null; // EffectivenessControlId/EffectivenessControlCode
  gapDescription: string;
  riskDescription: string;
  gapRiskRanking: RiskRanking;
  riskRatingSeverityIndex: number | null;
  riskRatingLikelihoodIndex: number | null;
  gapType: GapType | null;
  gapOwner: string;
  processOwner: GapOwner;
  applications: GapApplication[];
  managementPrioritization: PriorityLevel | null;
  remediationRecommendation: string;
  managementResponse: string;
  gapClosureDate: string | null; // Only visible for Archived
  dateIdentified: string; // Previously "GAP Date"
  createdAt: string;
  riskAssessmentId: string;
}

// ==================== BACKEND TYPES ====================

interface GapBackend {
  Id: string;
  GapCode: string;
  Status: string;
  StatusRaw: number;
  CycleId: string;
  CycleCode: string;
  ProcessId: string;
  ProcessCode: string;
  Description: string;
  RiskDescription: string;
  RiskRating: string;
  RiskRatingRaw: number;
  RiskRatingSeverityIndex: number | null;
  RiskRatingLikelihoodIndex: number | null;
  Type: string;
  TypeRaw: number;
  Owner: string;
  ProcessOwnerId: string;
  ProcessOwner: string;
  EffectivenessControlId: string;
  EffectivenessControlCode: string;
  ControlIds: string;
  ControlCodes: string;
  CompensatingControlCodes: string;
  CompensatingControlIds: string;
  ObjectiveIds: string;
  ObjectiveCodes: string;
  ObjectiveProcessCodes: string;
  ApplicationIds: string;
  ApplicationNames: string;
  ManagementPrioritization: string;
  ManagementPrioritizationRaw: number;
  RemediationRecommendation: string;
  ManagementResponse: string;
  GapClosureDate: string | null;
  GapDate: string;
  CreatedAt: string;
  RiskAssessmentId: string;
}

interface GetGapsBackendResponse {
  result: GapBackend[];
  count: number;
}

// ==================== MAPPING FUNCTIONS ====================

/**
 * Map backend status to frontend status
 */
const mapStatus = (status: string): GapStatus => {
  const normalized = status.toLowerCase();
  if (normalized === 'open') return 'Open';
  if (normalized === 'archived') return 'Archived';
  return 'Draft';
};

/**
 * Map frontend status to API status
 */
export const mapStatusToApi = (status: GapStatus): GapStatusApi => {
  switch (status) {
    case 'Open': return 'open';
    case 'Archived': return 'archived';
    default: return 'draft';
  }
};

/**
 * Map backend gap type to frontend
 */
const mapGapType = (type: string | null | undefined): GapType | null => {
  if (!type) return null;
  const normalized = type.toLowerCase();
  if (normalized === 'design') return 'Design';
  if (normalized === 'effectiveness') return 'Effectiveness';
  if (normalized === 'other') return 'Other';
  return null;
};

/**
 * Map frontend gap type to API
 */
export const mapGapTypeToApi = (type: GapType | null): GapTypeApi | null => {
  switch (type) {
    case 'Design': return 'design';
    case 'Effectiveness': return 'effectiveness';
    case 'Other': return 'other';
    default: return null;
  }
};

/**
 * Map backend priority to frontend
 * null/undefined/empty -> null (not selected)
 * high -> High, med -> Med, low -> Low, unranked -> Unranked
 */
const mapPriority = (priority: string | null | undefined): PriorityLevel | null => {
  if (!priority) return null;
  const normalized = priority.toLowerCase();
  if (normalized === 'high') return 'High';
  if (normalized === 'med') return 'Med';
  if (normalized === 'low') return 'Low';
  if (normalized === 'unranked') return 'Unranked';
  return null;
};

/**
 * Map frontend priority to API
 * null -> null (not selected)
 * High -> high, Med -> med, Low -> low, Unranked -> unranked
 */
export const mapPriorityToApi = (priority: PriorityLevel | null): PriorityLevelApi | null => {
  switch (priority) {
    case 'High': return 'high';
    case 'Med': return 'med';
    case 'Low': return 'low';
    case 'Unranked': return 'unranked';
    default: return null;
  }
};

/**
 * Map backend Gap to frontend Gap
 */
const mapBackendToGap = (backend: GapBackend): Gap => {
  // Parse control IDs and codes (these are the compensating controls shown in UI)
  const controlIds = parseCommaSeparated(backend.ControlIds);
  const controlCodes = parseCommaSeparated(backend.ControlCodes);

  const compensatingControlIds = parseCommaSeparated(backend.CompensatingControlIds);
  const compensatingControlCodes = parseCommaSeparated(backend.CompensatingControlCodes);

  const relatedControls: GapControl[] = controlIds.map((id, index) => ({
    id,
    code: controlCodes[index] || '',
    name: '',
    type: 'direct' as const,
  }));

  const compensatingControls: GapControl[] = compensatingControlIds.map((id, index) => ({
    id,
    code: compensatingControlCodes[index] || '',
    name: '',
    type: 'direct' as const,
  }));

  // Parse effectiveness control (single control)
  const effectiveControl = backend.EffectivenessControlId && backend.EffectivenessControlId !== '-'
    ? { id: backend.EffectivenessControlId, code: backend.EffectivenessControlCode || '' }
    : null;

  // Parse objectives - using tilde-separated format like ControlActivities
  // ObjectiveIds = "id1~id2~id3"
  // ObjectiveCodes = "01~02~03" (objective numbers)
  // ObjectiveProcessCodes = "A01~A01~A02" (process codes)
  const objectiveIds = parseCommaSeparated(backend.ObjectiveIds);
  const objectiveCodes = parseCommaSeparated(backend.ObjectiveCodes);
  const objectiveProcessCodes = parseCommaSeparated(backend.ObjectiveProcessCodes);
  
  const relatedObjectives: GapObjective[] = objectiveIds.map((id, index) => {
    const processCode = objectiveProcessCodes[index] || '';
    const objectiveNumber = objectiveCodes[index] || '';
    // Combine to create display code: "A01.01"
    const code = processCode ? `${processCode}.${objectiveNumber}` : objectiveNumber;
    return {
      id,
      code,
      processCode,
      objectiveNumber,
    };
  });

  // Parse applications
  const applicationIds = parseCommaSeparated(backend.ApplicationIds);
  const applicationNames = parseCommaSeparated(backend.ApplicationNames);
  const applications: GapApplication[] = applicationIds.map((id, index) => ({
    id,
    name: applicationNames[index] || '',
  }));

  return {
    id: backend.Id,
    status: mapStatus(backend.Status),
    gapId: backend.GapCode || '',
    cycle: {
      id: backend.CycleId,
      code: backend.CycleCode,
      name: '',
    },
    linkedProcess: {
      id: backend.ProcessId,
      code: backend.ProcessCode,
      name: '',
      riskAssessmentId: backend.RiskAssessmentId
    },
    relatedObjectives,
    relatedControls,
    compensatingControls,
    effectiveControl,
    gapDescription: backend.Description || '',
    riskDescription: backend.RiskDescription || '',
    gapRiskRanking: mapRiskRanking(backend.RiskRating),
    riskRatingSeverityIndex: backend.RiskRatingSeverityIndex ?? null,
    riskRatingLikelihoodIndex: backend.RiskRatingLikelihoodIndex ?? null,
    gapType: mapGapType(backend.Type),
    gapOwner: backend.Owner || '',
    processOwner: {
      id: backend.ProcessOwnerId || '',
      name: backend.ProcessOwner || '',
      avatar: undefined,
    },
    applications,
    managementPrioritization: mapPriority(backend.ManagementPrioritization),
    remediationRecommendation: backend.RemediationRecommendation || '',
    managementResponse: backend.ManagementResponse || '',
    gapClosureDate: backend.GapClosureDate || null,
    dateIdentified: backend.GapDate || '',
    createdAt: backend.CreatedAt || '',
    riskAssessmentId: backend.RiskAssessmentId
  };
};

// ==================== API REQUEST/RESPONSE TYPES ====================

export interface GetGapsRequest {
  annualReportId: string;
  // Filter parameters (use "Raw" fields for enum filtering per BE guidance)
  // All filters come from local filter state - navigation params are mapped to filters
  status?: GapStatus[];
  cycleIds?: string[];
  processIds?: string[];
  gapRiskRanking?: string[];
  gapType?: GapType[];
  processOwnerIds?: string[];
  managementPrioritization?: string[];
  dateIdentified?: string;
  gapDateQuarter?: string[];
  gapClosureDateQuarter?: string[];
  search?: SyncfusionSearchParam[];
  skip?: number;
  take?: number;
  sorted?: { name: string; direction: 'ascending' | 'descending' }[];
}

export interface GetGapsResponse {
  data: Gap[];
  totalCount: number;
}

/**
 * Create Gap Request - matches swagger API spec
 * POST /gaps
 */
export interface CreateGapRequest {
  status: GapStatusApi;
  description: string;
  riskDescription: string;
  riskRating: RiskRankingApi;
  riskRatingSeverityIndex: number | null;
  riskRatingLikelihoodIndex: number | null;
  type: GapTypeApi | null;
  owner: string;
  processId: string;
  controlIds: string[];
  objectiveIds: string[];
  applicationIds: string[];
  managementPrioritization: PriorityLevelApi | null;
  remediationRecommendation: string;
  managementResponse: string;
  dateIdentified: string | null; // ISO format: "2025-12-15T10:49:00.705Z" or null
  gapClosureDate: string | null; // ISO format: "2025-12-15T10:49:00.705Z" or null
}

export interface CreateGapResponse {
  data: {
    id: string;
    gapCode: string;
  };
  code: string;
  message: string;
}

/**
 * Update Gap Request - matches swagger API spec
 * PUT /gaps
 */
export interface UpdateGapRequest {
  gapId: string;
  status: GapStatusApi;
  description: string;
  riskDescription: string;
  riskRating: RiskRankingApi;
  riskRatingSeverityIndex: number | null;
  riskRatingLikelihoodIndex: number | null;
  type: GapTypeApi | null;
  owner: string;
  processId: string;
  controlIds: string[];
  objectiveIds: string[];
  applicationIds: string[];
  managementPrioritization: PriorityLevelApi | null;
  remediationRecommendation: string;
  managementResponse: string;
  dateIdentified: string | null; // ISO format: "2025-12-15T10:49:00.705Z" or null
  gapClosureDate: string | null; // ISO format: "2025-12-15T10:49:00.705Z" or null
}

export interface UpdateGapResponse {
  data: {
    id: string;
    gapCode: string;
  };
  code: string;
  message: string;
}

// ==================== API FUNCTIONS ====================

// Mapping functions for Raw enum fields (backend uses numeric values)
const gapStatusToRaw = (status: string): number => {
  const map: Record<string, number> = { 'Draft': 0, 'Open': 1, 'Archived': 2 };
  return map[status] ?? 0;
};

const gapTypeToRaw = (type: string): number => {
  const map: Record<string, number> = { 'Design': 0, 'Effectiveness': 1, 'Other': 2 };
  return map[type] ?? 0;
};

const gapRiskRankingToRaw = (ranking: string): number => {
  const map: Record<string, number> = { 'Unranked': 0, 'Low': 1, 'Med': 2, 'High': 3 };
  return map[ranking] ?? 0;
};

const managementPrioritizationToRaw = (prioritization: string): number => {
  const map: Record<string, number> = { 'Unranked': 0, 'Low': 1, 'Med': 2, 'High': 3 };
  return map[prioritization] ?? 0;
};

// ========== FILTER BUILDING UTILITIES ==========
// New Syncfusion filter structure from BE:
// - OR conditions: Use nested "predicates" array with "condition": "or"
// - AND conditions: Separate objects in "where" array (default)

type FilterValue = string | boolean | number | null;

interface FilterCondition {
  field: string;
  operator: string;
  value: FilterValue;
  condition?: 'and' | 'or';
}

interface FilterPredicateGroup {
  predicates: FilterCondition[];
}

type WhereCondition = FilterCondition | FilterPredicateGroup;

/**
 * Get all gaps for an annual report
 * Default sort: Descending by GapDate (Newest first)
 * Note: Status filter is handled by backend by default
 * Optional processId/cycleId filters for navigation from Process Hierarchy
 */
export const getAllGaps = async (request: GetGapsRequest): Promise<GetGapsResponse> => {
  try {
    // Build filter conditions using new Syncfusion structure
    const {
      whereConditions,
      addMultiValueFilter,
      addRawMultiValueFilter,
      addSingleValueFilter,
    } = buildFilterConditions();

    // Drawer filters (all filters come from local filter state, navigation params are mapped to filters)
    // Status (use StatusRaw with numeric values)
    addRawMultiValueFilter('StatusRaw', request.status, gapStatusToRaw);
    
    // Cycle IDs
    addMultiValueFilter('CycleId', request.cycleIds);
    
    // Process IDs
    addMultiValueFilter('ProcessId', request.processIds);
    
    if (request.gapRiskRanking?.includes('Unranked')) {
      request.gapRiskRanking.push("null");
    }

    // GAP Risk Ranking (use RiskRatingRaw with numeric values)
    addRawMultiValueFilter('RiskRatingRaw', request.gapRiskRanking, gapRiskRankingToRaw);
    
    // GAP Type (use TypeRaw with numeric values)
    addRawMultiValueFilter('TypeRaw', request.gapType, gapTypeToRaw);

    // Management Prioritization (use ManagementPrioritizationRaw with numeric values)
    addRawMultiValueFilter('ManagementPrioritizationRaw', request.managementPrioritization, managementPrioritizationToRaw);

    // Process Owner (filter by ProcessOwnerId)
    addMultiValueFilter('ProcessOwnerId', request.processOwnerIds);

    // Drawer filter: Date Identified (equal operator)
    addSingleValueFilter('GapDate', request.dateIdentified);

    // Quarter filters: GapDateQuarter and GapClosureDateQuarter
    if (request.gapDateQuarter && request.gapClosureDateQuarter) {
      // Both provided → OR predicate across different fields
      let filters = request.gapDateQuarter.map(q => 
          ({ field: 'GapDateQuarter', operator: 'equal', value: q, condition: 'or' as const }),
      );
      request.gapClosureDateQuarter.forEach(q => {
        filters.push({ field: 'GapClosureDateQuarter', operator: 'equal', value: q, condition: 'or' as const });
      });
      
      whereConditions.push({
          predicates: filters,
        });
      
    } else if (request.gapDateQuarter) {
      if (request.gapDateQuarter.length == 1) {
        addSingleValueFilter('GapDateQuarter', request.gapDateQuarter[0]);
      }
      else {
        let predicate = request.gapDateQuarter.map(q => 
          ({ field: 'GapDateQuarter', operator: 'equal', value: q, condition: 'or' as const })
        );        
        whereConditions.push({
        predicates: predicate as FilterCondition[]})
      }  
    } else if (request.gapClosureDateQuarter) {
      if (request.gapClosureDateQuarter.length == 1) {
        addSingleValueFilter('GapClosureDateQuarter', request.gapClosureDateQuarter[0]);
      }
      else {
        let predicate = request.gapClosureDateQuarter.map(q => 
          ({ field: 'GapClosureDateQuarter', operator: 'equal', value: q, condition: 'or' as const })
        );        
        whereConditions.push({
        predicates: predicate as FilterCondition[]})
      }
    }

    const requestBody: {
      skip: number;
      take: number;
      requiresCounts: boolean;
      sorted?: { name: string; direction: 'ascending' | 'descending' }[];
      where?: WhereCondition[];
      search?: SyncfusionSearchParam[];
    } = {
      skip: request.skip || 0,
      take: request.take || 1000,
      requiresCounts: true,
      // Only include sorted param if provided (let BE decide default)
      ...(request.sorted && { sorted: request.sorted }),
      // Add where conditions if any filters are specified
      ...(whereConditions.length > 0 && { where: whereConditions }),
      // Add search param if provided
      ...(request.search && { search: request.search }),
    };
    const response = await apiRequest<GetGapsBackendResponse, typeof requestBody>(
      'POST',
      `/gaps/index-grid/${request.annualReportId}`,
      requestBody,
      true // Authentication required
    );

    // Map backend data to frontend format
    const mappedData = response.result.map(mapBackendToGap);

    return {
      data: mappedData,
      totalCount: response.count,
    };
  } catch (error) {
    console.error('Failed to fetch gaps:', error);
    if (error && typeof error === 'object' && 'code' in error) {
      throw error;
    }
    throw {
      code: 'UNEXPECTED_ERROR',
      message: error instanceof Error ? error.message : 'Failed to fetch gaps',
    };
  }
};

/**
 * Build filter conditions for Syncfusion grid API
 * - Multiple values for same field: grouped with OR using predicates
 * - Single values: direct filter condition
 * - Different fields: AND logic (default)
 */
const buildFilterConditions = () => {
  const whereConditions: WhereCondition[] = [];

  // Add filter for multiple string values (OR within same field)
  const addMultiValueFilter = (field: string, values: string[] | undefined) => {
    if (!values?.length) return;
    
    if (values.length === 1) {
      // Single value - direct condition
      whereConditions.push({
        field,
        operator: 'equal',
        value: values[0],
      });
    } else {
      // Multiple values - use predicates with OR
      whereConditions.push({
        predicates: values.map(value => ({
          field,
          operator: 'equal',
          value,
          condition: 'or' as const,
        })),
      });
    }
  };

  // Add filter for multiple numeric values using Raw mapping (OR within same field)
  const addRawMultiValueFilter = (field: string, values: string[] | undefined, toRawFn: (v: string) => number | null) => {
    if (!values?.length) return;
    
    if (values.length === 1) {
      // Single value - direct condition
      whereConditions.push({
        field,
        operator: 'equal',
        value: toRawFn(values[0]),
      });
    } else {
      // Multiple values - use predicates with OR
      whereConditions.push({
        predicates: values.map(value => ({
          field,
          operator: 'equal',
          value: value != "null" ? toRawFn(value) : null,
          condition: 'or' as const,
        })),
      });
    }
  };

  // Add filter for single boolean/value
  const addSingleValueFilter = (field: string, value: FilterValue | undefined) => {
    if (value === null || value === undefined) return;
    
    whereConditions.push({
      field,
      operator: 'equal',
      value,
    });
  };

  return {
    whereConditions,
    addMultiValueFilter,
    addRawMultiValueFilter,
    addSingleValueFilter,
  };
};

/**
 * Create a new gap
 * @param request - CreateGapRequest object matching API spec
 */
export const createGap = async (
  request: CreateGapRequest
): Promise<{ id: string; gapCode: string }> => {
  try {
    const response = await apiRequest<CreateGapResponse, CreateGapRequest>(
      'POST',
      '/gaps',
      request,
      true
    );

    return {
      id: response.data.id,
      gapCode: response.data.gapCode,
    };
  } catch (error) {
    console.error('Failed to create gap:', error);
    if (error && typeof error === 'object' && 'code' in error) {
      throw error;
    }
    throw {
      code: 'UNEXPECTED_ERROR',
      message: error instanceof Error ? error.message : 'Failed to create gap',
    };
  }
};

/**
 * Update an existing gap
 * @param request - UpdateGapRequest object matching API spec
 */
export const updateGap = async (
  request: UpdateGapRequest
): Promise<{ id: string; gapCode: string }> => {
  try {
    const response = await apiRequest<UpdateGapResponse, UpdateGapRequest>(
      'PUT',
      '/gaps',
      request,
      true
    );

    return {
      id: response.data.id,
      gapCode: response.data.gapCode,
    };
  } catch (error) {
    console.error('Failed to update gap:', error);
    if (error && typeof error === 'object' && 'code' in error) {
      throw error;
    }
    throw {
      code: 'UNEXPECTED_ERROR',
      message: error instanceof Error ? error.message : 'Failed to update gap',
    };
  }
};

/**
 * Delete a gap
 */
export const deleteGap = async (gapId: string): Promise<void> => {
  try {
    await apiRequest(
      'DELETE',
      `/gaps/${gapId}`,
      undefined,
      true
    );
  } catch (error) {
    console.error('Failed to delete gap:', error);
    if (error && typeof error === 'object' && 'code' in error) {
      throw error;
    }
    throw {
      code: 'UNEXPECTED_ERROR',
      message: error instanceof Error ? error.message : 'Failed to delete gap',
    };
  }
};
